import { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { ShieldCheck, Clock, CheckCircle, XCircle, ChevronLeft, ChevronRight, Eye, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('admin_token');
const api = (method, url, data) => axios({ method, url: `${API_URL}/api${url}`, data, headers: { Authorization: `Bearer ${getToken()}` } });

export default function AdminKYCPage() {
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [reviewDialog, setReviewDialog] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const fetchQueue = async (pg = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: statusFilter, page: pg, limit: 10 });
      const res = await api('get', `/admin/kyc?${params}`);
      setSubmissions(res.data.submissions || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setPage(pg);
    } catch (e) {
      toast.error('Failed to fetch KYC queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQueue(); }, [statusFilter]);

  const openReview = async (kycId) => {
    try {
      const res = await api('get', `/admin/kyc/${kycId}`);
      setReviewDialog(res.data.submission);
    } catch (e) {
      toast.error('Failed to load KYC details');
    }
  };

  const handleReview = async (action) => {
    if (!reviewDialog) return;
    if (action === 'rejected' && !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await api('put', `/admin/kyc/${reviewDialog.id}`, { action, reason: rejectionReason });
      toast.success(`KYC ${action}!`);
      setReviewDialog(null);
      setRejectionReason('');
      fetchQueue(page);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const StatusBadge = ({ status }) => {
    const config = {
      pending: { icon: Clock, className: 'bg-[#F2A900]/20 text-[#F2A900]' },
      approved: { icon: CheckCircle, className: 'bg-green-100 text-green-700' },
      rejected: { icon: XCircle, className: 'bg-red-100 text-red-700' },
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return <Badge className={c.className}><Icon className="w-3 h-3 mr-1" />{status}</Badge>;
  };

  return (
    <div data-testid="admin-kyc-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#2D2424]">KYC Review Queue</h1>
          <p className="text-sm text-[#78716C]">{total} submissions</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="rounded-xl border border-[#E7E5E4] bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-6 h-6 border-2 border-[#D9381E] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F9F5F0] border-b border-[#E7E5E4]">
                  <th className="text-left p-3 font-semibold">User</th>
                  <th className="text-left p-3 font-semibold">Submitted</th>
                  <th className="text-left p-3 font-semibold">Status</th>
                  <th className="text-left p-3 font-semibold">Reviewer</th>
                  <th className="text-left p-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map(sub => (
                  <tr key={sub.id} className="border-b border-[#E7E5E4]/50 hover:bg-[#F9F5F0]/50">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-[#D9381E]/10 flex items-center justify-center text-xs font-bold text-[#D9381E]">
                          {sub.user_name?.[0] || '?'}
                        </div>
                        <span className="font-medium">{sub.user_name || sub.user?.name || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="p-3 text-[#78716C]">{new Date(sub.submitted_at).toLocaleDateString()}</td>
                    <td className="p-3"><StatusBadge status={sub.status} /></td>
                    <td className="p-3 text-[#78716C]">{sub.reviewer_name || '-'}</td>
                    <td className="p-3">
                      <Button size="sm" variant="outline" onClick={() => openReview(sub.id)} className="rounded-lg" data-testid={`review-kyc-${sub.id}`}>
                        <Eye className="w-3 h-3 mr-1" /> Review
                      </Button>
                    </td>
                  </tr>
                ))}
                {submissions.length === 0 && (
                  <tr><td colSpan={5} className="p-12 text-center text-[#78716C]">No submissions found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-[#E7E5E4]">
            <span className="text-xs text-[#78716C]">Page {page} of {pages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => fetchQueue(page - 1)} disabled={page <= 1}><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => fetchQueue(page + 1)} disabled={page >= pages}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </Card>

      {/* Review Dialog */}
      <Dialog open={!!reviewDialog} onOpenChange={() => { setReviewDialog(null); setRejectionReason(''); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>KYC Document Review</DialogTitle></DialogHeader>
          {reviewDialog && (
            <div className="space-y-4">
              {/* User Info */}
              <div className="flex items-center gap-3 p-3 bg-[#F9F5F0] rounded-lg">
                <User className="w-5 h-5 text-[#D9381E]" />
                <div>
                  <div className="font-medium">{reviewDialog.user?.name}</div>
                  <div className="text-xs text-[#78716C]">{reviewDialog.user?.city}, {reviewDialog.user?.state} | {reviewDialog.user?.religion}</div>
                </div>
              </div>

              {/* Documents Side by Side */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-[#2D2424] mb-2">Aadhaar Front</p>
                  {reviewDialog.front_image && (
                    <img src={reviewDialog.front_image} alt="Front" className="w-full rounded-lg border border-[#E7E5E4]" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-[#2D2424] mb-2">Aadhaar Back</p>
                  {reviewDialog.back_image && (
                    <img src={reviewDialog.back_image} alt="Back" className="w-full rounded-lg border border-[#E7E5E4]" />
                  )}
                </div>
              </div>

              {reviewDialog.status === 'pending' && (
                <>
                  <Textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Rejection reason (required if rejecting)" rows={2} />
                  <DialogFooter className="gap-2">
                    <Button onClick={() => handleReview('rejected')} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="reject-kyc-btn">
                      <XCircle className="w-4 h-4 mr-1" /> Reject
                    </Button>
                    <Button onClick={() => handleReview('approved')} className="bg-green-600 hover:bg-green-700 text-white" data-testid="approve-kyc-btn">
                      <CheckCircle className="w-4 h-4 mr-1" /> Approve
                    </Button>
                  </DialogFooter>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
